import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  CreateKnowledgebaseParams,
  DeleteKnowledgeBaseParams,
  GetKnowledgeBasesParams,
  ListKnowledgeBasesParams,
  UpdateKnowledgeBaseParams,
} from '~/features/knowledge-bases/types'
import { getQueryKey, useSuspenseInfiniteQuery, useSuspenseQuery } from '~/services'
import { getApiClient } from '~/stores'
import { listAll } from '~/utils'

export const useKnowledgeBase = ({ workspaceId, botId, kbId }: GetKnowledgeBasesParams) => {
  const { data, ...rest } = useSuspenseQuery('workspaces_/$workspaceId_/bots_/$botId_/knowledgeBases_/$kbId_', {
    workspaceId,
    botId,
    kbId,
  })

  return { ...rest, data: { ...data, oldKbId: data.tags['oldKbId'] } }
}

export const useListKnowledgeBase = ({ workspaceId, botId }: ListKnowledgeBasesParams) => {
  const { data, ...rest } = useSuspenseInfiniteQuery('workspaces_/$workspaceId_/bots_/$botId_/knowledgeBases_', {
    workspaceId,
    botId,
  })

  return {
    ...rest,
    data: data.pages.flatMap((page) => page.knowledgeBases.map((kb) => ({ ...kb, oldKbId: kb.tags['oldKbId'] }))),
  }
}

export function useCreateKnowledgeBase() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ name, workspaceId, botId }: CreateKnowledgebaseParams) =>
      await getApiClient({ workspaceId, botId }).createKnowledgeBase({ name }),
    onSuccess: async (_, { workspaceId, botId }) => {
      await queryClient.invalidateQueries({
        queryKey: getQueryKey('workspaces_/$workspaceId_/bots_/$botId_/knowledgeBases_', {
          workspaceId,
          botId,
        }),
      })
    },
  })
}

export function useUpdateKnowledgeBase() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ workspaceId, botId, onSuccess: _onSuccess, ...props }: UpdateKnowledgeBaseParams) => {
      return await getApiClient({ workspaceId, botId }).updateKnowledgeBase({ ...props })
    },
    onSuccess: async (_, { workspaceId, botId }) => {
      await queryClient.invalidateQueries({
        queryKey: getQueryKey('workspaces_/$workspaceId_/bots_/$botId_/knowledgeBases_', {
          workspaceId,
          botId,
        }),
      })
    },
  })
}

export function useDeleteKnowledgeBase() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ workspaceId, botId, id: kbId, onSuccess: _onSuccess }: DeleteKnowledgeBaseParams) => {
      const currentState = await getApiClient({ workspaceId, botId }).getState({
        id: botId,
        type: 'bot',
        name: 'dsData',
      })
      const newPayload = currentState.state?.payload || {}
      delete newPayload[kbId]

      const updateState = getApiClient({ workspaceId, botId }).setState({
        id: botId,
        type: 'bot',
        name: 'dsData',
        payload: newPayload,
      })

      const knowledgeSources = await listAll(
        getApiClient({ workspaceId, botId }).listFiles,
        { tags: { kbId: kbId } },
        ({ files }) => files
      )
      const deleteKnowledgeSources = Promise.all(
        knowledgeSources.map((file) => getApiClient({ workspaceId, botId }).deleteFile({ id: file.id }))
      )
      const deleteKnowledgeBase = getApiClient({ workspaceId, botId }).deleteKnowledgeBase({ id: kbId })

      return await Promise.all([deleteKnowledgeSources, deleteKnowledgeBase, updateState])
    },
    onSuccess: async (_, { workspaceId, botId, id: kbId, onSuccess: _onSuccess }) => {
      await queryClient.invalidateQueries({
        queryKey: getQueryKey('workspaces_/$workspaceId_/bots_/$botId_/knowledgeBases_', {
          workspaceId,
          botId,
        }),
      })
      await queryClient.invalidateQueries({
        predicate: (query) => query.queryKey.includes(kbId),
      })
      await queryClient.invalidateQueries({
        queryKey: getQueryKey('workspaces_/$workspaceId_/bots_/$botId_/states/$name', {
          workspaceId,
          botId,
          name: 'dsData',
        }),
      })
      _onSuccess?.()
    },
  })
}
